function edgeNodes(feature) {
  const properties = feature?.properties || {};
  const parts = String(properties.edge_uid || feature?.id || '').split('_');
  return [properties.u ?? parts[0], properties.v ?? parts[1]].map((id) => String(id ?? ''));
}

export function shortNodeName(name, nodeId, maxLength = 28) {
  const fallback = `Nodo ${nodeId}`;
  const normalized = String(name || fallback)
    .replace(/\s*\/\s*/g, ' / ')
    .replace(/\s+/g, ' ')
    .trim();
  if (normalized.length <= maxLength) return normalized;

  const firstStreet = normalized.split(' / ')[0];
  const candidate = firstStreet.length <= maxLength ? firstStreet : normalized;
  return `${candidate.slice(0, Math.max(1, maxLength - 1)).trimEnd()}…`;
}

export function extractGraphNodes(geojson) {
  const explicit = (geojson?.features || [])
    .filter((feature) => feature.geometry?.type === 'Point' && feature.properties?.kind === 'graph_node')
    .map((feature) => {
      const id = String(feature.properties.node_id ?? feature.id);
      const name = feature.properties.name || `Nodo ${id}`;
      return {
        id,
        name,
        label: shortNodeName(name, id),
        lon: Number(feature.geometry.coordinates[0]),
        lat: Number(feature.geometry.coordinates[1]),
      };
    });
  if (explicit.length) {
    return Array.from(new Map(explicit.map((node) => [node.id, node])).values());
  }

  const nodes = new Map();
  (geojson?.features || []).filter((feature) => feature.geometry?.type === 'LineString').forEach((feature) => {
    const coordinates = feature.geometry.coordinates || [];
    if (coordinates.length < 2) return;
    const [u, v] = edgeNodes(feature);
    const name = feature.properties?.name;
    [[u, coordinates[0]], [v, coordinates[coordinates.length - 1]]].forEach(([id, coordinate]) => {
      if (!id) return;
      const current = nodes.get(id) || { id, names: new Set(), lon: coordinate[0], lat: coordinate[1] };
      if (name && name !== 'Strada') current.names.add(String(name));
      nodes.set(id, current);
    });
  });
  return Array.from(nodes.values()).map(({ id, names, lon, lat }) => {
    const name = names.size ? Array.from(names).sort().join(' / ') : `Nodo ${id}`;
    return {
      id,
      name,
      label: shortNodeName(name, id),
      lon: Number(lon),
      lat: Number(lat),
    };
  });
}

export function materializeNewRoadFeatures(roads, nodes) {
  const byId = new Map((nodes || []).map((node) => [String(node.id), node]));
  return (roads || []).flatMap((road) => {
    const originId = String(road.origin ?? road.graph_u ?? road.origin_zone_id ?? '');
    const destinationId = String(road.destination ?? road.graph_v ?? road.destination_zone_id ?? '');
    const origin = byId.get(originId);
    const destination = byId.get(destinationId);
    const coordinates = road.geometry?.coordinates || (origin && destination
      ? [[origin.lon, origin.lat], [destination.lon, destination.lat]]
      : null);
    if (!coordinates) return [];
    const uid = String(road.uid || road.temp_id);
    const make = (edgeUid, u, v, coords, oppositeUid) => ({
      type: 'Feature',
      id: edgeUid,
      properties: {
        edge_uid: edgeUid,
        u,
        v,
        graph_u: u,
        graph_v: v,
        name: road.name || `${origin?.name || `Nodo ${u}`} – ${destination?.name || `Nodo ${v}`}`,
        highway: road.highway || 'residential',
        lanes: Number(road.lanes) || 1,
        speed_kph: Number(road.speed ?? road.speed_kph) || 30,
        capacity_vph: Number(road.capacity ?? road.capacity_vph) || 600,
        flow: Number(road.flow) || 0,
        is_new: true,
        opposite_uid: oppositeUid || null,
      },
      geometry: { type: 'LineString', coordinates: coords },
    });
    const reverseUid = `${uid}_reverse`;
    const result = [make(uid, originId, destinationId, coordinates, (road.twoWay || road.two_way) ? reverseUid : null)];
    if (road.twoWay || road.two_way) result.push(make(reverseUid, destinationId, originId, [...coordinates].reverse(), uid));
    return result;
  });
}

export function normalizePersistedRoad(road) {
  return {
    ...road,
    uid: road.uid || road.temp_id,
    origin: String(road.origin ?? road.graph_u ?? road.origin_zone_id ?? ''),
    destination: String(road.destination ?? road.graph_v ?? road.destination_zone_id ?? ''),
    speed: Number(road.speed ?? road.speed_kph) || 30,
    capacity: Number(road.capacity ?? road.capacity_vph) || 600,
    factor: Number(road.factor ?? road.capacity_factor) || 1,
    twoWay: !!(road.twoWay ?? road.two_way),
  };
}

function normalizeScenarioFeature(feature) {
  const properties = feature?.properties || {};
  const scenarioFlow = properties.flow_scenario;
  return {
    ...feature,
    properties: {
      ...properties,
      // The assignment API preserves the observed/base value in `flow` and
      // publishes the recalculated distribution in `flow_scenario`. Map
      // styling and tooltips consume `flow`, so expose the scenario value
      // there once the result is displayed.
      flow: scenarioFlow == null ? Number(properties.flow || 0) : Number(scenarioFlow),
    },
  };
}

function applyEdgeDelta(feature, properties, deltaByUid) {
  const delta = deltaByUid.get(String(feature.properties?.edge_uid));
  if (!delta) return properties;
  const scenarioFlow = delta.flow_scenario;
  return {
    ...properties,
    flow: scenarioFlow == null ? Number(properties.flow || 0) : Number(scenarioFlow),
    flow_scenario: scenarioFlow == null ? properties.flow_scenario : Number(scenarioFlow),
    flow_base: delta.flow_base,
    delta_flow: Number(delta.delta_flow || 0),
  };
}

export function mergeScenarioResult(baseGeoJson, resultGeoJson, edgeDeltas = []) {
  const deltaByUid = new Map(
    (edgeDeltas || []).map((row) => [String(row.edge_uid), row]),
  );
  if (!resultGeoJson?.features) return null;
  if (!baseGeoJson?.features) {
    return {
      ...resultGeoJson,
      features: resultGeoJson.features.map((feature) => {
        const normalized = normalizeScenarioFeature(feature);
        return {
          ...normalized,
          properties: applyEdgeDelta(feature, normalized.properties, deltaByUid),
        };
      }),
    };
  }
  const resultByUid = new Map(
    resultGeoJson.features.map((feature) => [feature.properties?.edge_uid, feature]),
  );
  const baseUids = new Set(baseGeoJson.features.map((feature) => feature.properties?.edge_uid));
  const mergedBase = baseGeoJson.features.map((feature) => {
    const result = resultByUid.get(feature.properties?.edge_uid);
    if (!result) {
      return {
        ...feature,
        properties: applyEdgeDelta(feature, {
          ...feature.properties,
          flow: 0,
          flow_scenario: 0,
        }, deltaByUid),
      };
    }
    const normalizedResult = normalizeScenarioFeature(result);
    return {
      ...feature,
      geometry: normalizedResult.geometry || feature.geometry,
      properties: applyEdgeDelta(
        feature,
        { ...feature.properties, ...normalizedResult.properties },
        deltaByUid,
      ),
    };
  });
  const created = resultGeoJson.features.filter(
    (feature) => !baseUids.has(feature.properties?.edge_uid),
  ).map(normalizeScenarioFeature);
  return { ...resultGeoJson, features: [...mergedBase, ...created] };
}
